import React, { useState, useEffect } from 'react';
import { ServiceStatus, AIConfig } from '../../types';

interface EnvironmentWidgetProps {
  status: ServiceStatus | null;
  aiConfig: AIConfig | null;
  mode?: 'environment' | 'resources';
  apiBaseUrl?: string;
}

const EnvironmentWidget: React.FC<EnvironmentWidgetProps> = ({ status, aiConfig, mode = 'environment', apiBaseUrl }) => {
  const [resources, setResources] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'resources' && apiBaseUrl) {
      fetchResources();
    }
  }, [mode, apiBaseUrl]);

  const fetchResources = async () => {
    if (!apiBaseUrl) return;

    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/env/all`);
      const data = await res.json();
      const firstNode = Object.keys(data)[0];

      if (!firstNode) return;
      const node = data[firstNode];
      setResources(node);

      // Decide if what we just loaded looks stale
      const docker = node?.resources?.docker?.data || {};
      const cloud = node?.resources?.cloud?.data || {};
      const looksStale =
        (!docker.orchestrator && (cloud.provider === 'local' || !cloud.platform || docker.in_docker === false));

      // Only auto-refresh once per tab/session
      const FLAG = 'tw_hw_auto_refreshed';
      if (looksStale && !sessionStorage.getItem(FLAG)) {
        sessionStorage.setItem(FLAG, 'true');

        // kick off refresh, then poll until fresh or timeout
        try {
          await fetch(`${apiBaseUrl}/env/refresh`, { method: 'POST' });
        } catch (e) {
          console.error('Auto-refresh POST failed:', e);
          return; // don't loop if refresh call failed
        }

        // Poll /env/all up to ~5 seconds (10 x 500ms) or until no longer stale
        const start = Date.now();
        const deadlineMs = 5000;
        const intervalMs = 500;

        while (Date.now() - start < deadlineMs) {
          try {
            const fres = await fetch(`${apiBaseUrl}/env/all`);
            const fdata = await fres.json();
            const fnodeKey = Object.keys(fdata)[0];
            if (!fnodeKey) break;

            const fnode = fdata[fnodeKey];
            const d = fnode?.resources?.docker?.data || {};
            const c = fnode?.resources?.cloud?.data || {};
            const fresh =
              !!d.orchestrator || (c?.provider && c.provider !== 'local') || (c?.platform && c.platform !== 'unknown');

            if (fresh) {
              setResources(fnode);
              break;
            }
          } catch (e) {
            // ignore transient fetch errors during polling
          }
          await new Promise(r => setTimeout(r, intervalMs));
        }
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!apiBaseUrl) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/env/refresh`, { method: 'POST' });
      const data = await response.json();
      const firstNode = Object.keys(data)[0];
      if (firstNode) setResources(data[firstNode]);
    } catch (err) {
      console.error('Failed to refresh resources:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatServiceName = (key: string) => {
    let formatted = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    if (formatted === 'Db') formatted = 'DB';
    if (formatted.startsWith('Ai')) formatted = formatted.replace(/^Ai/, 'AI');
    return formatted;
  };

  // find the latest detected_at across all resource rows
  const latestDetectedAt = (res: any): string | null => {
    try {
      const entries = Object.values(res?.resources || {}) as any[];
      const times = entries
        .map((e: any) => e?.detected_at ? new Date(e.detected_at).getTime() : 0)
        .filter((t: number) => t > 0);
      if (!times.length) return null;
      return new Date(Math.max(...times)).toLocaleString();
    } catch {
      return null;
    }
  };

  if (mode === 'resources') {
    if (loading) {
      return (
        <div className="dashboard-widget">
          <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            Detecting system resources...
          </p>
        </div>
      );
    }

    if (!resources) {
      return (
        <div className="dashboard-widget">
          <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No resources detected
          </p>
        </div>
      );
    }

    return (
      <div className="dashboard-widget">
        <div className="status-list">
          {/* CPU */}
          {resources.resources?.cpu?.data && (
            <div className="status-pill">
              <span className="status-dot" style={{ backgroundColor: '#38a169' }} />
              <span>
                CPU: {resources.resources.cpu.data.physical_cores} physical / {resources.resources.cpu.data.logical_cores} logical @{' '}
                {(resources.resources.cpu.data.cpu_freq_mhz ?? resources.resources.cpu.data.cpu_freq ?? 0).toFixed(2)} MHz
              </span>
            </div>
          )}

          {/* Memory Info */}
          {resources.resources?.memory?.data && (
            <div className="status-pill">
              <span className="status-dot" style={{ backgroundColor: '#38a169' }} />
              {(() => {
                const m = resources.resources.memory.data;

                // New schema (preferred)
                const hostTotal = m.host_total_gb ?? null;
                const hostAvail = m.host_available_gb ?? null;
                const hostUsed = m.host_used_gb ?? (hostTotal != null && hostAvail != null ? hostTotal - hostAvail : null);
                const hostPct = m.host_percent ?? null;

                // Container limit (new schema)
                const limit = m.container_limit_gb ?? null;

                // Legacy schema (fallback)
                const legacyTotal = m.total_gb ?? null;
                const legacyUsed = m.used_gb ?? null;
                const legacyAvail = m.available_gb ?? null;
                const legacyPct = m.percent ?? null;

                // Choose best totals
                const total = (limit ?? hostTotal ?? legacyTotal ?? 0);
                // Choose best used
                const used =
                  (hostUsed != null ? hostUsed :
                    legacyUsed != null ? legacyUsed :
                      (hostTotal != null && hostAvail != null) ? (hostTotal - hostAvail) :
                        (legacyTotal != null && legacyAvail != null) ? (legacyTotal - legacyAvail) :
                          0);

                // Choose best percent
                const pct = (hostPct != null ? hostPct :
                  legacyPct != null ? legacyPct :
                    total ? (used / total) * 100 : 0);

                return (
                  <span>
                    Memory: {used.toFixed(2)}/{total.toFixed(1)} GB ({Math.round(pct)}%)
                  </span>
                );
              })()}
            </div>
          )}

          {/* Environment */}
          {(() => {
            const d = resources.resources?.docker?.data || {};
            let label = 'Bare Metal';
            let color = '#38a169';

            if (d.orchestrator === 'cloud_run') { label = 'Cloud Run Container'; color = '#3b82f6'; }
            else if (d.orchestrator === 'kubernetes') { label = 'Kubernetes Pod'; color = '#6366f1'; }
            else if (d.orchestrator === 'docker-desktop') { label = 'Docker Desktop VM'; color = '#fbbf24'; }
            else if (d.in_docker) { label = 'Docker Container'; color = '#fbbf24'; }

            return (
              <div className="status-pill">
                <span className="status-dot" style={{ backgroundColor: color }} />
                <span>Environment: {label}</span>
              </div>
            );
          })()}

          {/* Cloud */}
          {(resources.provider || resources.resources?.cloud?.data || resources.resources?.docker?.data) && (() => {
            const nodeProvider = resources.provider;                 // node-level (authoritative)
            const cloud = resources.resources?.cloud?.data;          // may be stale until refresh
            const orch = resources.resources?.docker?.data?.orchestrator;

            let label = 'local';
            if (orch === 'cloud_run') label = 'GCP Cloud Run';
            else if (nodeProvider === 'gcp') label =
              cloud?.platform === 'gce' ? 'GCP Compute Engine' :
                cloud?.platform === 'gke' ? 'GCP GKE' : 'GCP';
            else if (nodeProvider === 'aws') label = 'AWS EC2';
            else if (nodeProvider === 'azure') label = 'Azure VM';

            return (
              <div className="status-pill">
                <span className="status-dot" style={{ backgroundColor: '#38a169' }} />
                <span>Cloud: {label}</span>
              </div>
            );
          })()}

          {/* GPU */}
          {resources.resources?.gpu?.data && (() => {
            const g = resources.resources.gpu.data;
            const name =
              g.available
                ? (g.devices && g.devices[0]?.name) || 'GPU Available'
                : g.note || (g.devices && g.devices[0]?.note) || 'Not detected';
            return (
              <div className="status-pill">
                <span className="status-dot" style={{ backgroundColor: g.available ? '#38a169' : '#e53e3e' }} />
                <span>GPU: {name}</span>
              </div>
            );
          })()}

          {/* Software (show only if present) */}
          {resources.resources?.software?.data?.postgresql && (
            <div className="status-pill">
              <span className="status-dot" style={{ backgroundColor: '#38a169' }} />
              <span>PostgreSQL: {resources.resources.software.data.postgresql}</span>
            </div>
          )}
          {resources.resources?.software?.data?.py_fastapi && (
            <div className="status-pill">
              <span className="status-dot" style={{ backgroundColor: '#38a169' }} />
              <span>FastAPI: {resources.resources.software.data.py_fastapi}</span>
            </div>
          )}
          {resources.resources?.software?.data?.py_uvicorn && (
            <div className="status-pill">
              <span className="status-dot" style={{ backgroundColor: '#38a169' }} />
              <span>Uvicorn: {resources.resources.software.data.py_uvicorn}</span>
            </div>
          )}

          {/* Refresh */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={handleRefresh}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh Detection
            </button>
            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Last detected: {latestDetectedAt(resources) ?? 'Unknown'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Default: environment mode
  return (
    <div className="dashboard-widget">
      {status ? (
        <div className="status-list">
          {Object.entries(status)
            .filter(([key]) => key.toLowerCase() !== 'environment')
            .map(([key, value]) => (
              <div className="status-pill" key={key}>
                <span
                  className="status-dot"
                  style={{
                    backgroundColor:
                      key === 'ai_provider' ? (value && status.ai_ready ? '#38a169' : '#e53e3e') :
                        (typeof value === 'object' && value !== null && Object.keys(value).length > 0) ||
                          value === 'ok' || value === 'true' || value === true ||
                          value === 'connected' || value === 'ready'
                          ? '#38a169' : '#e53e3e'
                  }}
                />
                <span>{formatServiceName(key)}</span>
              </div>
            ))}
        </div>
      ) : (
        <p style={{ fontSize: '1rem', color: '#666', textAlign: 'center', padding: '2rem' }}>
          Checking system health...
        </p>
      )}

      {aiConfig && (
        <div
          style={{
            marginTop: '2rem',
            padding: '1rem',
            backgroundColor: 'rgba(248, 249, 250, 0.9)',
            borderRadius: '8px',
            fontSize: '1rem',
            border: '1px solid rgba(0,0,0,0.1)'
          }}
        >
          <div style={{ fontSize: '1rem', marginBottom: '0.5rem', fontWeight: 600, color: '#2d3748' }}>
            <strong>AI Model:</strong> {aiConfig.default_model}
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 600 }}>
            <strong>Configured:</strong>{' '}
            {aiConfig.testwerk_api_key_configured ? (
              <span style={{ color: '#28a745', marginLeft: '0.5rem', fontWeight: 'bold' }}>Yes</span>
            ) : (
              <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontWeight: 'bold' }}>No</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentWidget;
